import { OFFICES_URL_PREFIX, SEARCH_BY_COUNTY_SUFFIX } from '../consts';
import { IOffice } from '../model/office.model';
import { getOfficesByCountyPageSheetQueries } from './i18n.utils';
import { slugifySearchQuery } from './searchQuery.utils';

export type OfficeSearchOption = {
  text: string;
  county: string;
  city: string;
};

export const computeOfficeSearchOptions = (
  offices: IOffice[],
  groupedByCity = false
): OfficeSearchOption[] => {
  const officesSortedByCity = [...offices].sort((a, b) =>
    `${a.city}-${a.name}`.localeCompare(`${b.city}-${b.name}`)
  );
  return officesSortedByCity.map((office) => ({
    text: `${office.name}${groupedByCity ? '' : ' (' + office.city + ')'}`,
    county: office.province,
    city: office.city,
  }));
};

const generateUrlInternal = (
  province: string,
  city: string,
  name: string
): string => {
  const cityQueries = getOfficesByCountyPageSheetQueries(true).filter(
    (q) => q.split('/').length > 1
  );
  const cityInQueries = cityQueries.find((q) =>
    q.split('/')[1].includes(slugifySearchQuery(city))
  );
  if (cityInQueries) {
    return `/${OFFICES_URL_PREFIX}/${
      slugifySearchQuery(province) + SEARCH_BY_COUNTY_SUFFIX
    }/${slugifySearchQuery(city)}/${slugifySearchQuery(name)}`;
  }
  return `/${OFFICES_URL_PREFIX}/${
    slugifySearchQuery(province) + SEARCH_BY_COUNTY_SUFFIX
  }/${slugifySearchQuery(name)}`;
};

export const generateUrl = (office: IOffice): string =>
  generateUrlInternal(office.province, office.city, office.name);

export const generateUrlFromOfficeSearchOption = (
  officeSearchOption: OfficeSearchOption
): string =>
  generateUrlInternal(
    officeSearchOption.county,
    officeSearchOption.city,
    officeSearchOption.text
  );

export const getOfficesByCountyAndCity = (
  offices: IOffice[],
  county: string,
  city?: string
) => {
  const result = [...offices]
    .filter(
      (office) =>
        slugifySearchQuery(office.province) ===
        slugifySearchQuery(county.replace(SEARCH_BY_COUNTY_SUFFIX, ''))
    )
    .filter((office) =>
      city ? slugifySearchQuery(office.city) === slugifySearchQuery(city) : true
    );
  result.sort((a, b) => a.name.localeCompare(b.name));
  return result;
};

const computeOfficesByKey = (
  offices: IOffice[],
  key: 'province' | 'city',
  getOffices: (offices: IOffice[], county: string, city?: string) => IOffice[]
): [string, IOffice[]][] => {
  const county = offices[0].province;
  const result: [string, IOffice[]][] = Array.from(
    new Set(offices.map((office) => office[key]))
  )
    .reduce(
      (acc, value) => [
        ...acc,
        [
          value,
          key === 'province'
            ? getOffices(offices, value)
            : getOffices(offices, county, value),
        ],
      ],
      []
    )
    .filter(([, offices]) => offices.length > 0);
  result.sort((a, b) => {
    if (b[1].length - a[1].length != 0) {
      return b[1].length - a[1].length;
    }
    return a[0].localeCompare(b[0]);
  });
  return result;
};

export const computeOfficesByCountyArray = (
  offices: IOffice[]
): [string, IOffice[]][] => {
  return computeOfficesByKey(offices, 'province', getOfficesByCountyAndCity);
};

export const computeOfficesByCityArray = (
  offices: IOffice[]
): [string, IOffice[]][] => {
  return computeOfficesByKey(offices, 'city', getOfficesByCountyAndCity);
};
